import moment from 'moment'
import { useSearchParams } from 'react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import {
  listWithdrawalRequests,
  setWithdrawalRequestClosed,
  type WithdrawalFilters
} from '@/api/processes-api'

export const WITHDRAWALS_PAGE_SIZE = 25

export function useWithdrawalRequests(
  ecommerceId: string | undefined,
  offset: number,
  filters: WithdrawalFilters
) {
  return useQuery({
    queryKey: ['withdrawal-requests', ecommerceId, offset, filters],
    queryFn: () =>
      listWithdrawalRequests(
        ecommerceId ?? '',
        offset,
        WITHDRAWALS_PAGE_SIZE,
        filters
      ),
    enabled: !!ecommerceId,
    placeholderData: keepPreviousData
  })
}

export function useSetWithdrawalClosed(ecommerceId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, closed }: { id: string; closed: boolean }) =>
      setWithdrawalRequestClosed(ecommerceId ?? '', id, closed),
    // The status is worked out by the list, so it's read again.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['withdrawal-requests', ecommerceId]
      })
  })
}

// The filter bar's selection, which lives in the URL.
export function useWithdrawalFilters() {
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')
  const to = searchParams.get('to')
  return {
    statuses: searchParams.getAll('status') as NonNullable<
      WithdrawalFilters['statuses']
    >,
    // The API compares the raw bound, so a bare date would cut the last day at midnight.
    receivedFrom: from ? moment(from).startOf('day').toISOString() : undefined,
    receivedTo: to ? moment(to).endOf('day').toISOString() : undefined,
    search: searchParams.get('search')?.trim() || undefined
  } satisfies WithdrawalFilters
}
